import { groupBy, isEmpty, mapValues, omit } from './fp';
export * from './analytics';

// R.keys returns [] for a nil input, so the ramda version of renameKeys mapped
// null / undefined to {} instead of throwing.
const renameWith = (keysMap, obj) =>
  obj == null
    ? {}
    : Object.keys(obj).reduce((acc, key) => {
        acc[keysMap[key] || key] = obj[key];
        return acc;
      }, {});

// curried: renameKeys(map)(obj) or renameKeys(map, obj). Uses arguments.length
// like R.curry, so renameKeys(map, undefined) still evaluates (to {}) rather
// than returning the curried function.
export function renameKeys(keysMap, obj) {
  return arguments.length < 2 ? o => renameWith(keysMap, o) : renameWith(keysMap, obj);
}

export const withIndex = items => items.map((value, index) => ({ ...value, index }));

export const countNumberOf = items =>
  items.reduce((sum, { values = [] }) => sum + values.length, 0);

export const makeTree = items => {
  const hierarchy = items.reduce((index, item) => {
    if (item.parent in index) {
      return { ...index, [item.parent]: [...index[item.parent], item.id], [item.id]: [] };
    }
    return { ...index, [item.id]: [] };
  }, {}); //=> E.g. {"1":[2],"2":[3],"3":[],"4":[]}
  const index = mapValues(
    group => group[0],
    groupBy(item => item.id, items),
  ); //=> E.g. {"1": <item1>, "2": <item2>, ...}
  const assoc = id => {
    const childNodes = id => hierarchy[id].map(childId => makeNode(childId));
    const kids = childNodes(id);
    return isEmpty(kids) ? index[id] : { ...index[id], childNodes: kids };
  };
  const makeNode = id => omit(['parent'], assoc(id));
  return items.filter(item => item.parent == null).map(item => makeNode(item.id));
};

export const computeHasSpotlight = values => {
  const LIMIT = 8;
  if (values.length > LIMIT) return true;

  const computeflatArray = (acc, item) => {
    if (item.childNodes) return item.childNodes.reduce(computeflatArray, acc);
    return [...acc, item.id];
  };

  return values.reduce(computeflatArray, []).length > LIMIT;
};

export const isPerfectMatch = str =>
  isEmpty(
    str.replace(/<mark>(.*?)<\/mark>/g, '').replace(/[ ~!@#$%^&*(){}[\]`/=?+|\-_;:'",<.>]/gm, ''),
  );

export const cleanifyLayoutIds = (layoutIds = {}) => ({
  header: [],
  sections: [],
  rows: [],
  ...layoutIds,
});

export const getSiteUrl = () => {
  return window.CONFIG?.siteUrl || window.location.origin;
};

export const getBasename = () => {
  try {
    const siteUrl = getSiteUrl();
    if (!siteUrl) return '/';
    const url = new URL(siteUrl, window.location.origin);
    return url.pathname.replace(/\/+$/, '') || '/';
  } catch {
    return '/';
  }
};

export async function sha256(str) {
  const data = new TextEncoder().encode(str);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return [...new Uint8Array(hash)].map(b => b.toString(16).padStart(2, '0')).join('');
}
